import { useState, useCallback } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { recalculationMonthlyCutsService } from '@/api/domains/recalculation';
import type { OpenCutYear, OpenCutMonth, OpenCutDescription, RecalculationProcessParams } from '@/api/domains/recalculation';
import type { UseEmployeeDimensionalFilterResult } from '@/hooks/attendance/common/useEmployeeDimensionalFilter';


const MONTH_I18N_KEYS: Record<number, string> = {
  1: 'january', 2: 'february', 3: 'march', 4: 'april',
  5: 'may', 6: 'june', 7: 'july', 8: 'august',
  9: 'september', 10: 'october', 11: 'november', 12: 'december',
};

export interface UseRecalculoFiltersResult {
  selectedYear: string | null;
  selectedMonth: string | null;
  selectedDescriptionId: string | null;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  descriptionOptions: { value: string; label: string }[];
  isLoadingYears: boolean;
  isLoadingMonths: boolean;
  isLoadingDescriptions: boolean;
  onYearChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  handleRecalculate: () => void;
}

interface UseRecalculoFiltersConfig {
  onRecalculate: (params: RecalculationProcessParams) => void;
  dimensionalFilterState: UseEmployeeDimensionalFilterResult;
}

export function useRecalculoFilters(
  { onRecalculate, dimensionalFilterState }: UseRecalculoFiltersConfig,
): UseRecalculoFiltersResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedDescriptionId, setSelectedDescriptionId] = useState<string | null>(null);

  const yearCatalog = useCatalogLoader<OpenCutYear>(
    (signal) => recalculationMonthlyCutsService.getOpenYears(signal),
    (item) => ({ value: String(item.year), label: String(item.year) }),
    [],
    false,
    (response) => showApiError(response, translate('attendance:recalculo.errors.loadYears')),
  );

  const monthCatalog = useCatalogLoader<OpenCutMonth>(
    (signal) => recalculationMonthlyCutsService.getOpenMonths(Number(selectedYear), signal),
    (item) => ({ value: String(item.month), label: translate(`common:months.${MONTH_I18N_KEYS[item.month]}`) }),
    [selectedYear],
    !selectedYear,
    (response) => showApiError(response, translate('attendance:recalculo.errors.loadMonths')),
  );

  const descriptionCatalog = useCatalogLoader<OpenCutDescription>(
    (signal) => recalculationMonthlyCutsService.getOpenDescriptions(
      Number(selectedYear),
      Number(selectedMonth),
      signal,
    ),
    (item) => ({ value: String(item.monthlyCutId), label: item.description }),
    [selectedYear, selectedMonth],
    !selectedYear || !selectedMonth,
    (response) => showApiError(response, translate('attendance:recalculo.errors.loadDescriptions')),
  );

  const onYearChange = useCallback((value: string) => {
    setSelectedYear(value);
    setSelectedMonth(null);
    setSelectedDescriptionId(null);
  }, []);

  const onMonthChange = useCallback((value: string) => {
    setSelectedMonth(value);
    setSelectedDescriptionId(null);
  }, []);

  const onDescriptionChange = useCallback((value: string) => {
    setSelectedDescriptionId(value);
  }, []);

  const handleRecalculate = useCallback(() => {
    if (!selectedDescriptionId) return;
    const { selectedEmployeeIds, dimensionalFilters } = dimensionalFilterState;
    onRecalculate({
      monthlyCutId: Number(selectedDescriptionId),
      employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : null,
      companyIds: dimensionalFilters.companyIds,
      areaIds: dimensionalFilters.areaIds,
      positionIds: dimensionalFilters.positionIds,
      costCenterIds: dimensionalFilters.costCenterIds,
      locationIds: dimensionalFilters.locationIds,
      contractModalityIds: dimensionalFilters.contractModalityIds,
      groupIds: dimensionalFilters.groupIds,
      employeeStatus: dimensionalFilters.status,
    });
  }, [selectedDescriptionId, dimensionalFilterState, onRecalculate]);

  return {
    selectedYear,
    selectedMonth,
    selectedDescriptionId,
    yearOptions: yearCatalog.options,
    monthOptions: monthCatalog.options,
    descriptionOptions: descriptionCatalog.options,
    isLoadingYears: !yearCatalog.isLoaded,
    isLoadingMonths: !monthCatalog.isLoaded && !!selectedYear,
    isLoadingDescriptions: !descriptionCatalog.isLoaded && !!selectedYear && !!selectedMonth,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    handleRecalculate,
  };
}

export default useRecalculoFilters;
